import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  FaGithub, FaWpforms, FaDiscord, FaYoutube, FaHtml5, FaCss3Alt,
  FaReact, FaNodeJs, FaVuejs
} from "react-icons/fa";
import {
  SiJavascript, SiTailwindcss, SiTypescript, SiMongodb
} from "react-icons/si";
import { IoGitPullRequest, IoStatsChart } from "react-icons/io5";
import { MdSearch, MdEmail } from "react-icons/md";
import myImage from '../assets/pfp.png';

const Particles = () => {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let particles = [];
    const particleCount = 100;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    class Particle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.size = Math.random() * 2 + 1;
        this.speedX = Math.random() * 3 - 1.5;
        this.speedY = Math.random() * 3 - 1.5;
        this.color = `hsla(${Math.random() * 360}, 100%, 80%, 0.7)`;
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.size > 0.2) this.size -= 0.01;
      }
      draw() {
        ctx.shadowBlur = 6;
        ctx.shadowColor = this.color;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const init = () => {
      particles = [];
      for (let i = 0; i < particleCount; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        particles.push(new Particle(x, y));
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
        if (particles[i].size <= 0.3) {
          particles.splice(i, 1);
          i--;
          const x = Math.random() * canvas.width;
          const y = Math.random() * canvas.height;
          particles.push(new Particle(x, y));
        }
      }
      requestAnimationFrame(animate);
    };

    init();
    animate();
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      init();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return <canvas ref={canvasRef} className="absolute top-0 left-0 w-full h-full -z-10" />;
};

const Hero = () => (
  <section className="relative min-h-screen flex items-center justify-center text-white bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] overflow-hidden">
    <Particles />
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm -z-10" />
    <motion.div className="text-center px-4 z-10" initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
      <motion.h1 className="text-5xl md:text-7xl font-extrabold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-fuchsia-500 to-indigo-400 drop-shadow-[0_0_15px_rgba(168,85,247,0.4)]">Welcome!</motion.h1>
      <motion.p className="text-lg md:text-xl mb-8 max-w-2xl mx-auto text-gray-300">Hey there! I'm NotReal003. Welcome to my corner of the internet where I showcase my journey as a developer and creator.</motion.p>
      <motion.a href="https://sentralia.notreal003.org" className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-pink-500/40 transition-all duration-300" whileHover={{ scale: 1.05, y: -5 }} whileTap={{ scale: 0.95 }}>
        <IoGitPullRequest className="text-xl" />
        Sentralia
      </motion.a>
    </motion.div>
  </section>
);

const BioSection = () => (
  <section className="bg-gradient-to-br from-gray-900 via-black to-gray-800 py-20 text-white overflow-hidden">
    <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-16">
      <motion.div className="md:w-1/3" initial={{ opacity: 0, x: -100 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
        <div className="relative">
          <img src={myImage} alt="NotReal003" className="rounded-full w-48 h-48 md:w-64 md:h-64 object-cover border-4 border-fuchsia-500 shadow-[0_0_30px_rgba(236,72,153,0.4)]" />
          <motion.div className="absolute inset-0 border-4 border-fuchsia-400 rounded-full" animate={{ scale: [1, 1.1, 1], opacity: [0, 0.5, 0] }} transition={{ repeat: Infinity, duration: 2 }} />
        </div>
      </motion.div>
      <motion.div className="md:w-2/3" initial={{ opacity: 0, x: 100 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
        <h2 className="text-4xl font-bold mb-6 text-indigo-300">About Me</h2>
        <p>
Hey! I'm NotNT, also known as NotReal003. I'm a gamer, content creator, and{" "}
<strong className="text-indigo-400">developer</strong>{" "}from India.
</p>

<p>
I enjoy building things for the web and have been learning and working with technologies like{" "}
<strong className="text-sky-400">React</strong>,{" "}
<strong className="text-yellow-400">JavaScript</strong>,{" "}
<strong className="text-orange-500">HTML</strong>, and{" "}
<strong className="text-blue-400">CSS</strong>.{" "}
I'm always experimenting with new ideas and improving my skills as I go.
</p>

<p>
I'm also a long-time{" "}
<strong className="text-green-400">Minecraft</strong>{" "}player. I used to play on the{" "}
<strong className="text-purple-400">NetherGames Network</strong>{" "}server, where I served as a{" "}
<strong className="text-red-400">staff member (crew)</strong>.{" "}
It was a great experience helping players and being part of a large gaming community.
</p>

<p>
Whether it's coding, gaming, or building new projects, I enjoy learning, creating, and sharing what I make with others.
</p>
        <p className="mt-6 text-lg text-gray-400">Want to connect? <a href="https://discord.com/users/1131271104590270606" className="text-blue-400 underline hover:text-white">Reach out!</a></p>
      </motion.div>
    </div>
  </section>
);

const CTASection = () => {
  const buttons = [
    { href: "https://github.com/NotReal003", icon: <FaGithub />, label: "GitHub" },
    { href: "https://request.notreal003.org/apply", icon: <FaWpforms />, label: "Sentralia Guild" },
    { href: "/player", icon: <MdSearch />, label: "Players" },
    { href: "/history", icon: <IoStatsChart />, label: "History" },
    { href: "https://youtube.com/@NotNT77", icon: <FaYoutube />, label: "YouTube" },
    { href: "https://notreal003.org/discord", icon: <FaDiscord />, label: "Discord" },
  ];
  return (
    <section className="bg-gradient-to-b from-slate-900 to-black text-white py-20">
      <div className="container mx-auto px-4 flex flex-wrap justify-center gap-6">
        {buttons.map((btn, i) => (
          <motion.a key={i} href={btn.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 px-6 py-3 rounded-full font-medium text-md bg-white/10 hover:bg-indigo-500/30 transition-all duration-300 shadow-lg backdrop-blur-md border border-white/10"
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
            <span className="text-xl text-indigo-300">{btn.icon}</span> {btn.label}
          </motion.a>
        ))}
      </div>
    </section>
  );
};

const ContactInfo = () => {
  const contacts = [
//    {
//      title: "Email Me",
//      description: "If you have any queries or wants to reach out to me, you may email to me by clicking here. (support@notreal003.org)",
//      icon: <MdEmail />,
//      url: "mailto:noreply.notreal003@gmail.com",
//    },
    {
      title: "Join our Discord Server",
      description: "You may join our Discord server to connect with us and also the community!",
      icon: <FaDiscord />,
      url: "https://notreal003.org/discord",
    },
  ];

  return (
    <section className="bg-gray-950 text-white py-8">
      <div className="container mx-auto px-4">
        <motion.h2 className="text-4xl font-bold text-center mb-12 text-indigo-300">Connect With Me</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {contacts.map((contact, i) => (
            <motion.a key={i} href={contact.url} target="_blank" className="bg-white/5 border border-white/10 backdrop-blur-md p-8 rounded-xl shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-2 transition-all duration-500 group"
              initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }}>
              <div className="text-4xl mb-6 text-center text-indigo-400">{contact.icon}</div>
              <h3 className="text-2xl font-bold text-center">{contact.title}</h3>
              <p className="text-center text-gray-400 mt-2">{contact.description}</p>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

const Features = () => {
  const projects = [
    {
      title: "Sentralia",
      description: "System for reports, support tickets, and applications with admin dashboard. Built in React + Node.js.",
      icon: <FaNodeJs />,
      url: "https://sentralia.notreal003.org",
    },
    {
      title: "Support HelpDesk",
      description: "Get answers via documentation, guides, and FAQs with ease.",
      icon: <FaVuejs />,
      url: "https://support.notreal003.org",
    },
  ];

  return (
    <section className="bg-gray-950 text-white py-20">
      <div className="container mx-auto px-4">
        <motion.h2 className="text-4xl font-bold text-center mb-12 text-indigo-300">My Projects</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {projects.map((project, i) => (
            <motion.a key={i} href={project.url} target="_blank" className="bg-white/5 border border-white/10 backdrop-blur-md p-8 rounded-xl shadow-lg hover:shadow-indigo-500/30 hover:-translate-y-2 transition-all duration-500 group"
              initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }}>
              <div className="text-4xl mb-6 text-center text-indigo-400">{project.icon}</div>
              <h3 className="text-2xl font-bold text-center">{project.title}</h3>
              <p className="text-center text-gray-400 mt-2">{project.description}</p>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

const Skills = () => {
  const skills = [
    { name: "React", icon: <FaReact />, color: "text-sky-400" },
    { name: "JavaScript", icon: <SiJavascript />, color: "text-yellow-400" },
    { name: "TypeScript", icon: <SiTypescript />, color: "text-blue-500" },
    { name: "MongoDB", icon: <SiMongodb />, color: "text-green-500" },
    { name: "TailwindCSS", icon: <SiTailwindcss />, color: "text-cyan-400" },
    { name: "HTML5", icon: <FaHtml5 />, color: "text-orange-500" },
    { name: "CSS", icon: <FaCss3Alt />, color: "text-blue-400" },
    { name: "Node.js", icon: <FaNodeJs />, color: "text-green-400" },
  ];

  return (
    <section className="bg-gradient-to-b from-slate-900 to-black text-white py-20">
      <div className="container mx-auto px-4 text-center">
        <motion.h2 className="text-4xl font-bold mb-16 text-indigo-300">Technologies I Use</motion.h2>
        <div className="flex flex-wrap justify-center gap-8">
          {skills.map(({ name, icon, color }, i) => (
            <motion.div key={i} className="flex flex-col items-center group" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
              <div className={`text-6xl p-4 rounded-full bg-white/5 backdrop-blur-md shadow-md transition-all ${color} group-hover:scale-110`}>
                {icon}
              </div>
              <p className="mt-2 text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">{name}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const App = () => (
  <div className="bg-black">
    <Hero />
    <BioSection />
    <CTASection />
    <Features />
    <ContactInfo />
    <Skills />
  </div>
);

export default App;
